import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@redux/hooks';

import { CandidateSuccessModal, ChoiceChip, FormDialog } from '@components';

import { Box, FormLabel, TextField } from '@mui/material';

import { candidateSubtitle, isStatusLoading, toggle } from '@utils';

import {
  candidateDetailsSelectors,
  sendCandidateQuestion,
  setCandidateDetailsAction,
} from '@redux/candidateDetails';

import {
  CandidateDetailsAction,
  MORE_INFO_TOPICS,
  SearchParams,
} from '@constants';

export const CandidateAskQuestionModal = () => {
  const dispatch = useAppDispatch();
  const [, setSearchParams] = useSearchParams();

  const { sendCandidateQuestionAPIStatus, writeError, data } = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsData,
  );
  const candidateDetailsAction = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsAction,
  );

  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState<boolean>(false);

  const [questionText, setQuestionText] = useState('');
  const [topics, setTopics] = useState<string[]>([]);

  const isLoading = isStatusLoading(sendCandidateQuestionAPIStatus);

  useEffect(() => {
    if (candidateDetailsAction) {
      if (
        [CandidateDetailsAction.ASK_QUESTION].includes(candidateDetailsAction)
      ) {
        setModalOpen(true);
      }
    } else {
      setModalOpen(false);
    }
  }, [candidateDetailsAction]);

  const handleModalClose = () => {
    dispatch(setCandidateDetailsAction(null));
    setSearchParams(
      (_searchParams) => {
        _searchParams.delete(SearchParams.ACTION);
        return _searchParams;
      },
      { replace: true },
    );
  };

  const onSubmitQuestion = useCallback(async () => {
    if (data?.id) {
      const res = await dispatch(
        sendCandidateQuestion({
          candidateId: data.id,
          text: [
            topics.length ? `Topics: ${topics.join(', ')}` : '',
            questionText,
          ]
            .filter(Boolean)
            .join('\n\n'),
        }),
      );

      if (sendCandidateQuestion.fulfilled.match(res)) {
        handleModalClose();
        setSuccessModalOpen(true);
      }
    }
  }, [questionText, topics, data?.id]);

  if (!data) return null;

  const header = {
    label: [data.profile?.firstName, data.profile?.lastName]
      .filter(Boolean)
      .join(' '),
    subLabel: candidateSubtitle(data),
  };

  return (
    <>
      <FormDialog
        isOpen={isModalOpen}
        onClose={handleModalClose}
        {...header}
        onSubmit={onSubmitQuestion}
        isLoading={isLoading}
        submitDisabled={!questionText.trim()}
        hint={questionText.trim() ? '' : 'Write your question to submit.'}
        error={writeError}
      >
        <Box my={2}>
          <FormLabel sx={{ fontSize: '0.75rem' }}>
            What would help you decide?
          </FormLabel>
          <Box display="flex" flexWrap="wrap" gap={0.75} mt={1} mb={2}>
            {MORE_INFO_TOPICS.map((topic) => {
              const selected = topics.includes(topic);

              return (
                <ChoiceChip
                  key={topic}
                  selected={selected}
                  onClick={() => setTopics(toggle(topics, topic))}
                >
                  {topic}
                </ChoiceChip>
              );
            })}
          </Box>
          <TextField
            label="Question"
            placeholder="Anything you'd wanna know about the candidate before interview"
            variant="outlined"
            fullWidth
            value={questionText}
            required
            onChange={(e) => setQuestionText(e.target.value)}
            disabled={isLoading}
            multiline
            minRows={3}
          />
        </Box>
      </FormDialog>
      <CandidateSuccessModal
        header={header}
        isOpen={isSuccessModalOpen}
        isPassed
        label="Thanks, we'll get back to you"
        subLabel="It may take some time to gather details from the candidate"
        handleClose={() => setSuccessModalOpen(false)}
      />
    </>
  );
};
